import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, SyutSelect, type SyutGridColumn, type ActionMenuItem } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { rolePermissionService } from 'api/services/rolePermissionService';
import type { DineApprovalRule, DineOutletAdmin } from 'api/types';

/**
 * Approval Rules (S3, crs-doc DineOps/DineOps-Counters-Shifts-and-Day-Close.md section 5) — which restaurant
 * approvals a property asks for, routed to which role, above what value. Shaped like the property approvals but
 * the restaurant's own (DineOps runs without CRS). No rule, no approval: nothing is asked by default.
 *
 * One rule for every property, and at most one per property; a property's own rule wins. A requisition at or
 * below the threshold goes straight through; above it, or with no threshold, it waits for a holder of the role.
 * The properties offered are those of the restaurant's own outlets.
 */

const TYPES = [{ value: 'REQUISITION', label: 'Requisition (before it goes to the kitchen)' }];
const EVERY = '__every__';
const money = (n?: number | null) => (n == null ? '' : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

interface FormState { scope: string; role: string; threshold: string; remarks: string; isActive: boolean }

const DineOpsApprovalRulesScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');
  const canDelete = allowedActions.includes('delete');

  const [rows, setRows] = useState<DineApprovalRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [roles, setRoles] = useState<{ value: string; label: string }[]>([]);
  const [properties, setProperties] = useState<{ value: string; label: string }[]>([]);
  const [form, setForm] = useState<{ record: DineApprovalRule | null; data: FormState } | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<DineApprovalRule | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try { setRows(await dineOpsService.getApprovalRules(true)); }
    catch (err: any) { setLoadError(err?.response?.data?.message ?? 'The approval rules could not be loaded.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    void load();
    rolePermissionService.getAll({ page: 1, pageSize: 200 })
      .then((res: any) => setRoles((res.data ?? []).filter((r: any) => r.roleCode && r.roleCode !== 'crs_system')
        .map((r: any) => ({ value: r.roleCode, label: `${r.roleName} (${r.roleCode})` }))))
      .catch(() => setRoles([]));
    dineOpsService.getOutlets(false).then((outlets: DineOutletAdmin[]) => {
      const seen = new Map<string, string>();
      for (const o of outlets) if (o.propertyId && !seen.has(o.propertyId)) seen.set(o.propertyId, o.propertyName ?? o.propertyId);
      setProperties([...seen.entries()].map(([value, label]) => ({ value, label })));
    }).catch(() => setProperties([]));
  }, [load]);

  const openForm = (record: DineApprovalRule | null) => {
    setFormError(null);
    setForm({
      record,
      data: record
        ? { scope: record.propertyId ?? EVERY, role: record.approverRole, threshold: record.thresholdAmount == null ? '' : String(record.thresholdAmount), remarks: record.remarks ?? '', isActive: record.isActive }
        : { scope: EVERY, role: '', threshold: '', remarks: '', isActive: true },
    });
  };

  const save = async () => {
    if (!form) return;
    const d = form.data;
    if (!d.role) { setFormError('Choose the role that approves.'); return; }
    const threshold = d.threshold.trim() === '' ? null : Number(d.threshold);
    if (threshold != null && (Number.isNaN(threshold) || threshold < 0)) { setFormError('The threshold is an amount of 0 or more, or empty for every one.'); return; }
    const body = {
      approvalType: 'REQUISITION' as const, propertyId: d.scope === EVERY ? null : d.scope, approverRole: d.role,
      thresholdAmount: threshold, remarks: d.remarks.trim() || null, isActive: d.isActive, rowVersion: form.record?.rowVersion ?? 0,
    };
    setSaving(true);
    setFormError(null);
    try {
      if (form.record) await dineOpsService.updateApprovalRule(form.record.id, body);
      else await dineOpsService.createApprovalRule(body);
      notificationService.toast.success('Approval rule saved. It applies to requisitions raised or changed from now on.');
      setForm(null);
      void load();
    } catch (err: any) {
      setFormError(err?.response?.data?.message ?? 'The rule could not be saved.');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await dineOpsService.deleteApprovalRule(deleting.id, deleting.rowVersion);
      notificationService.toast.success('Approval rule removed. Requisitions already awaiting approval keep their request.');
      setDeleting(null);
      void load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message ?? 'The rule could not be removed.');
      setDeleting(null);
    }
  };

  const columns = useMemo((): SyutGridColumn<DineApprovalRule>[] => [
    { id: 'type', header: 'Needs approval', accessorKey: 'approvalType', size: 160, cell: () => 'Requisition' },
    { id: 'scope', header: 'For', accessorKey: 'propertyName', size: 200, cell: ({ row }) => row.original.propertyName ?? <strong>Every property</strong> },
    { id: 'role', header: 'Approved by (role)', accessorKey: 'approverRole', size: 220, cell: ({ row }) => row.original.approverRoleName ? `${row.original.approverRoleName} (${row.original.approverRole})` : row.original.approverRole },
    { id: 'threshold', header: 'Above (transfer value)', accessorKey: 'thresholdAmount', size: 170,
      cell: ({ row }) => (row.original.thresholdAmount == null ? <span style={{ color: 'var(--text-muted)' }}>every one</span> : money(row.original.thresholdAmount)) },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', size: 100, cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
    { id: 'remarks', header: 'Remarks', accessorKey: 'remarks', size: 220, cell: ({ row }) => row.original.remarks ?? '—' },
    { id: 'updated', header: 'Last changed by', accessorKey: 'updatedByName', size: 160, cell: ({ row }) => row.original.updatedByName ?? '—' },
  ], []);

  const getRowActions = useCallback((row: DineApprovalRule): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (canEdit) items.push({ id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => openForm(row) });
    if (canDelete) items.push({ id: 'delete', label: 'Remove', icon: <IconSystem.trash size={16} />, onClick: () => setDeleting(row), variant: 'danger' });
    return items;
  }, [canEdit, canDelete]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: () => openForm(null), tooltip: 'Add a rule', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh', variant: 'icon' });
    return b;
  }, [canAdd, load]);

  const set = (patch: Partial<FormState>) => setForm((f) => (f ? { ...f, data: { ...f.data, ...patch } } : f));

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Approval Rules"
        description="Which restaurant approvals are asked, of which role, above what value. No rule, no approval"
        icon={<IconSystem.checkCircle size={18} />}
        actions={actionButtons}
      />
      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
          <SyutGrid data={rows} columns={columns} enableSorting frozenRightColumns={['actions']} isLoading={loading} rowActions={getRowActions}
            emptyMessage={loadError ?? 'No rules: requisitions go to the kitchen without approval. Add a rule to ask for one.'} />
        </div>
      </div>

      {form && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 560 }}>
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{form.record ? 'Edit approval rule' : 'Add approval rule'}</h2>
              <button className="modal-close" onClick={() => setForm(null)} disabled={saving}><IconSystem.x size={20} /></button>
            </div>
            <div className="modal-content">
              {formError && <div className="form-error-message" style={{ marginBottom: 12 }}>{formError}</div>}
              <div className="form-group">
                <SyutSelect label="Needs approval" options={TYPES} value="REQUISITION" onChange={() => undefined} size="sm" isDisabled />
              </div>
              <div className="form-group">
                <SyutSelect label="For" value={form.data.scope} size="sm" onChange={(v) => set({ scope: (v as string) || EVERY })}
                  options={[{ value: EVERY, label: 'Every property' }, ...properties]} />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>A property's own rule wins over the one for every property.</div>
              </div>
              <div className="form-group">
                <SyutSelect label="Approved by (role) *" value={form.data.role} size="sm" options={roles} placeholder="Choose a role"
                  onChange={(v) => set({ role: (v as string) || '' })} />
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Anyone holding this role may approve or reject, including the person who raised it.</div>
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="rule-threshold">Ask only above (transfer value)</label>
                <input id="rule-threshold" className="form-input" type="number" min={0} step="0.01" value={form.data.threshold}
                  onChange={(e) => set({ threshold: e.target.value })} placeholder="Empty = every requisition" />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="rule-remarks">Remarks</label>
                <input id="rule-remarks" className="form-input" value={form.data.remarks} maxLength={500} onChange={(e) => set({ remarks: e.target.value })}
                  placeholder="e.g., Agreed with the GM, October 2026" />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={form.data.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
                <span>Active</span>
              </label>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => setForm(null)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </>
      )}

      <ConfirmDialog open={!!deleting} title="Remove this rule?"
        message="Requisitions raised from now on will not ask for this approval. Those already awaiting approval keep their request."
        confirmLabel="Remove" cancelLabel="Keep it" variant="danger" onConfirm={() => void remove()} onCancel={() => setDeleting(null)} />
    </div>
  );
};

export default DineOpsApprovalRulesScreen;
